import React, { useState } from 'react';
import { Copy, CheckCircle, Palette, Square, Type, Layout, Maximize2, AlertCircle, Info, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import Navigation from '../../components/Navigation';
import StandardButton from '../../components/ui/StandardButton';
import StandardInput from '../../components/ui/StandardInput';
import StandardCard from '../../components/ui/StandardCard';
import { cn } from '../../utils/cn';
import Icon from '../../components/AppIcon';


const ComponentShowcaseDocumentation = () => {
  const [activeTab, setActiveTab] = useState('colors');
  const [copiedCode, setCopiedCode] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalSize, setModalSize] = useState('md');

  const copyToClipboard = (text, id) => {
    navigator.clipboard?.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Semantic Color Tokens from Tailwind Config
  const semanticColors = {
    brand: [
      { name: 'Primary', token: 'brand-primary', hex: '#0d9488', usage: 'Primary actions, links, brand identity' },
      { name: 'Primary Light', token: 'brand-primary-light', hex: '#5eead4', usage: 'Hover states, light backgrounds' },
      { name: 'Primary Dark', token: 'brand-primary-dark', hex: '#115e59', usage: 'Active states, dark accents' },
      { name: 'Secondary', token: 'brand-secondary', hex: '#388E3C', usage: 'Secondary actions, success states' },
      { name: 'Secondary Light', token: 'brand-secondary-light', hex: '#C8E6C9', usage: 'Light backgrounds, subtle highlights' },
      { name: 'Secondary Dark', token: 'brand-secondary-dark', hex: '#1B5E20', usage: 'Dark green accents' },
      { name: 'Accent', token: 'brand-accent', hex: '#14b8a6', usage: 'Accent elements, highlights' },
    ],
    background: [
      { name: 'Base', token: 'bg-base', hex: '#f9fafb', usage: 'Main page background' },
      { name: 'Surface', token: 'bg-surface', hex: '#ffffff', usage: 'Cards, panels, elevated surfaces' },
      { name: 'Elevated', token: 'bg-elevated', hex: '#ffffff', usage: 'Modals, dropdowns' },
      { name: 'Muted', token: 'bg-muted', hex: '#f3f4f6', usage: 'Subtle backgrounds, disabled states' },
    ],
    text: [
      { name: 'Primary', token: 'text-primary', hex: '#111827', usage: 'Headings, primary content' },
      { name: 'Secondary', token: 'text-secondary', hex: '#6b7280', usage: 'Body text, descriptions' },
      { name: 'Tertiary', token: 'text-tertiary', hex: '#9ca3af', usage: 'Placeholders, captions' },
      { name: 'Inverse', token: 'text-inverse', hex: '#ffffff', usage: 'Text on dark backgrounds' },
      { name: 'Link', token: 'text-link', hex: '#0d9488', usage: 'Hyperlinks' },
    ],
    border: [
      { name: 'Default', token: 'border-default', hex: '#e5e7eb', usage: 'Standard borders' },
      { name: 'Light', token: 'border-light', hex: '#f3f4f6', usage: 'Subtle dividers' },
      { name: 'Medium', token: 'border-medium', hex: '#d1d5db', usage: 'Emphasized borders' },
      { name: 'Focus', token: 'border-focus', hex: '#0d9488', usage: 'Focus states' },
    ],
    state: [
      { name: 'Success', token: 'state-success', hex: '#16a34a', usage: 'Success messages, positive feedback' },
      { name: 'Error', token: 'state-error', hex: '#dc2626', usage: 'Error messages, destructive actions' },
      { name: 'Warning', token: 'state-warning', hex: '#f59e0b', usage: 'Warning messages, caution' },
      { name: 'Info', token: 'state-info', hex: '#0284c7', usage: 'Info messages, neutral notifications' },
    ],
  };

  // Button States
  const buttonStates = [
    { variant: 'primary', label: 'Primary', states: ['default', 'hover', 'active', 'disabled'] },
    { variant: 'secondary', label: 'Secondary', states: ['default', 'hover', 'active', 'disabled'] },
    { variant: 'outline', label: 'Outline', states: ['default', 'hover', 'active', 'disabled'] },
    { variant: 'ghost', label: 'Ghost', states: ['default', 'hover', 'active', 'disabled'] },
    { variant: 'success', label: 'Success', states: ['default', 'hover', 'active', 'disabled'] },
    { variant: 'danger', label: 'Danger', states: ['default', 'hover', 'active', 'disabled'] },
  ];

  const buttonSizes = ['sm', 'md', 'lg'];

  // Form Input Examples
  const formInputs = [
    { type: 'text', label: 'Text Input', placeholder: 'Enter text...' },
    { type: 'email', label: 'Email Input', placeholder: 'email@example.com' },
    { type: 'password', label: 'Password Input', placeholder: '••••••••' },
  ];

  // Card Variants
  const cardVariants = [
    { variant: 'default', label: 'Default (Shadow)', description: 'Standard card with shadow elevation' },
    { variant: 'bordered', label: 'Bordered', description: 'Card with border instead of shadow' },
    { variant: 'elevated', label: 'Elevated', description: 'Card with larger shadow for emphasis' },
  ];

  // Modal Sizes
  const modalSizes = [
    { size: 'sm', label: 'Small', width: 'max-w-md' },
    { size: 'md', label: 'Medium', width: 'max-w-2xl' },
    { size: 'lg', label: 'Large', width: 'max-w-4xl' },
  ];

  const tabs = [
    { id: 'colors', label: 'Color Tokens', icon: Palette },
    { id: 'buttons', label: 'Buttons', icon: Square },
    { id: 'forms', label: 'Form Inputs', icon: Type },
    { id: 'cards', label: 'Cards', icon: Layout },
    { id: 'modals', label: 'Modals', icon: Maximize2 },
  ];

  const ColorTokenSection = () => (
    <div className="space-y-12">
      {Object.entries(semanticColors)?.map(([category, colors]) => (
        <div key={category}>
          <h3 className="text-2xl font-bold text-gray-900 mb-6 capitalize">{category} Tokens</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {colors?.map((color, index) => (
              <StandardCard key={index} variant="bordered" padding="none" className="overflow-hidden">
                <div
                  className="h-24 w-full cursor-pointer relative group"
                  style={{ backgroundColor: color?.hex }}
                  onClick={() => copyToClipboard(color?.hex, `color-${category}-${index}`)}
                >
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                    {copiedCode === `color-${category}-${index}` ? (
                      <CheckCircle className="text-white" size={28} />
                    ) : (
                      <Copy className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{color?.name}</h4>
                  <div className="space-y-1 mb-3">
                    <p className="text-sm text-gray-600">Token: <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{color?.token}</code></p>
                    <p className="text-sm text-gray-600">HEX: <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{color?.hex}</code></p>
                  </div>
                  <p className="text-xs text-gray-500">{color?.usage}</p>
                  <button
                    onClick={() => copyToClipboard(`bg-${color?.token}`, `token-${category}-${index}`)}
                    className="mt-3 text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                  >
                    {copiedCode === `token-${category}-${index}` ? (
                      <><CheckCircle size={14} /> Copied!</>
                    ) : (
                      <><Copy size={14} /> Copy Tailwind Class</>
                    )}
                  </button>
                </div>
              </StandardCard>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const ButtonSection = () => (
    <div className="space-y-12">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Button Variants & States</h3>
        <div className="space-y-8">
          {buttonStates?.map((button, idx) => (
            <StandardCard key={idx} padding="lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">{button?.label} Button</h4>
              <div className="flex flex-wrap gap-4 mb-6">
                <StandardButton variant={button?.variant}>Default</StandardButton>
                <StandardButton variant={button?.variant} className="hover:opacity-90">Hover</StandardButton>
                <StandardButton variant={button?.variant} className="active:scale-95">Active</StandardButton>
                <StandardButton variant={button?.variant} disabled>Disabled</StandardButton>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">React JSX</span>
                  <button
                    onClick={() => copyToClipboard(`<StandardButton variant="${button?.variant}">Button</StandardButton>`, `btn-${idx}`)}
                    className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                  >
                    {copiedCode === `btn-${idx}` ? (
                      <><CheckCircle size={14} /> Copied!</>
                    ) : (
                      <><Copy size={14} /> Copy Code</>
                    )}
                  </button>
                </div>
                <code className="text-sm text-gray-800">
                  {`<StandardButton variant="${button?.variant}">Button</StandardButton>`}
                </code>
              </div>
            </StandardCard>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Button Sizes</h3>
        <StandardCard padding="lg">
          <div className="flex flex-wrap items-center gap-4">
            {buttonSizes?.map((size, idx) => (
              <StandardButton key={idx} size={size} variant="primary">
                {size?.toUpperCase()} Button
              </StandardButton>
            ))}
          </div>
          <div className="bg-gray-50 p-4 rounded-lg mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">React JSX</span>
              <button
                onClick={() => copyToClipboard(`<StandardButton size="md">Button</StandardButton>`, 'btn-sizes')}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
              >
                {copiedCode === 'btn-sizes' ? (
                  <><CheckCircle size={14} /> Copied!</>
                ) : (
                  <><Copy size={14} /> Copy Code</>
                )}
              </button>
            </div>
            <code className="text-sm text-gray-800">
              {`<StandardButton size="sm|md|lg">Button</StandardButton>`}
            </code>
          </div>
        </StandardCard>
      </div>
    </div>
  );

  const FormSection = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Text Inputs</h3>
        <StandardCard padding="lg">
          <div className="space-y-6 max-w-2xl">
            {formInputs?.map((input, idx) => (
              <div key={idx}>
                <StandardInput
                  type={input?.type}
                  label={input?.label}
                  placeholder={input?.placeholder}
                  helperText="This is helper text for the input field"
                />
              </div>
            ))}
            <div>
              <StandardInput
                type="text"
                label="Input with Error"
                error="This field is required"
                placeholder="Enter value..."
              />
            </div>
            <div>
              <StandardInput
                type="text"
                label="Disabled Input"
                placeholder="Cannot edit..."
                disabled
              />
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">React JSX</span>
              <button
                onClick={() => copyToClipboard(`<StandardInput label="Label" placeholder="Placeholder..." />`, 'input-code')}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
              >
                {copiedCode === 'input-code' ? (
                  <><CheckCircle size={14} /> Copied!</>
                ) : (
                  <><Copy size={14} /> Copy Code</>
                )}
              </button>
            </div>
            <code className="text-sm text-gray-800">
              {`<StandardInput label="Label" placeholder="Placeholder..." />`}
            </code>
          </div>
        </StandardCard>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Textarea</h3>
        <StandardCard padding="lg">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
            <textarea
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
              rows="4"
              placeholder="Enter your message..."
            />
          </div>
        </StandardCard>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Select Dropdown</h3>
        <StandardCard padding="lg">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Option</label>
            <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200">
              <option>Option 1</option>
              <option>Option 2</option>
              <option>Option 3</option>
            </select>
          </div>
        </StandardCard>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Checkboxes & Radio Buttons</h3>
        <StandardCard padding="lg">
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Checkboxes</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500" />
                  <span className="text-sm text-gray-700">Option 1</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500" defaultChecked />
                  <span className="text-sm text-gray-700">Option 2 (Checked)</span>
                </label>
                <label className="flex items-center gap-2 cursor-not-allowed opacity-50">
                  <input type="checkbox" className="w-4 h-4 text-teal-600 border-gray-300 rounded" disabled />
                  <span className="text-sm text-gray-700">Option 3 (Disabled)</span>
                </label>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Radio Buttons</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="radio-group" className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-2 focus:ring-teal-500" />
                  <span className="text-sm text-gray-700">Option A</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="radio-group" className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-2 focus:ring-teal-500" defaultChecked />
                  <span className="text-sm text-gray-700">Option B (Selected)</span>
                </label>
                <label className="flex items-center gap-2 cursor-not-allowed opacity-50">
                  <input type="radio" name="radio-group" className="w-4 h-4 text-teal-600 border-gray-300" disabled />
                  <span className="text-sm text-gray-700">Option C (Disabled)</span>
                </label>
              </div>
            </div>
          </div>
        </StandardCard>
      </div>
    </div>
  );

  const CardSection = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Card Variants</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cardVariants?.map((card, idx) => (
            <StandardCard key={idx} variant={card?.variant} padding="lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">{card?.label}</h4>
              <p className="text-sm text-gray-600 mb-4">{card?.description}</p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <code className="text-xs text-gray-800">
                  {`variant="${card?.variant}"`}
                </code>
              </div>
            </StandardCard>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Card with Hover Effect</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StandardCard variant="default" padding="lg" hover>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Hover Card</h4>
            <p className="text-sm text-gray-600 mb-4">Hover over this card to see the shadow elevation effect</p>
            <div className="bg-gray-50 p-3 rounded-lg">
              <code className="text-xs text-gray-800">
                {`hover={true}`}
              </code>
            </div>
          </StandardCard>
          <StandardCard variant="bordered" padding="lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Standard Card</h4>
            <p className="text-sm text-gray-600 mb-4">This card has no hover effect for comparison</p>
            <div className="bg-gray-50 p-3 rounded-lg">
              <code className="text-xs text-gray-800">
                {`hover={false}`}
              </code>
            </div>
          </StandardCard>
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Card Padding Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {['none', 'sm', 'md', 'lg']?.map((pad, idx) => (
            <StandardCard key={idx} variant="bordered" padding={pad}>
              <div className="bg-teal-50 border-2 border-dashed border-teal-300 rounded">
                <p className="text-sm font-medium text-gray-900 text-center">padding="{pad}"</p>
              </div>
            </StandardCard>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Code Example</h3>
        <StandardCard padding="lg">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">React JSX</span>
              <button
                onClick={() => copyToClipboard(`<StandardCard variant="default" padding="lg">\n  <h4>Card Title</h4>\n  <p>Card content</p>\n</StandardCard>`, 'card-code')}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
              >
                {copiedCode === 'card-code' ? (
                  <><CheckCircle size={14} /> Copied!</>
                ) : (
                  <><Copy size={14} /> Copy Code</>
                )}
              </button>
            </div>
            <pre className="text-sm text-gray-800 overflow-x-auto">
              <code>{`<StandardCard variant="default" padding="lg">
  <h4>Card Title</h4>
  <p>Card content goes here</p>
</StandardCard>`}</code>
            </pre>
          </div>
        </StandardCard>
      </div>
    </div>
  );

  const ModalSection = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Modal Sizes</h3>
        <StandardCard padding="lg">
          <div className="flex flex-wrap gap-4">
            {modalSizes?.map((modal, idx) => (
              <StandardButton
                key={idx}
                variant="primary"
                onClick={() => {
                  setModalSize(modal?.size);
                  setShowModal(true);
                }}
              >
                Open {modal?.label} Modal
              </StandardButton>
            ))}
          </div>
        </StandardCard>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Modal States</h3>
        <StandardCard padding="lg">
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="text-green-600 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-semibold text-green-900">Success Modal</h4>
                <p className="text-sm text-green-700">Operation completed successfully</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-semibold text-red-900">Error Modal</h4>
                <p className="text-sm text-red-700">An error occurred during the operation</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="text-yellow-600 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-semibold text-yellow-900">Warning Modal</h4>
                <p className="text-sm text-yellow-700">Please review before proceeding</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="text-blue-600 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-semibold text-blue-900">Info Modal</h4>
                <p className="text-sm text-blue-700">Here is some important information</p>
              </div>
            </div>
          </div>
        </StandardCard>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Code Example</h3>
        <StandardCard padding="lg">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">React JSX - Modal Structure</span>
              <button
                onClick={() => copyToClipboard(`{showModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4">
      <h3>Modal Title</h3>
      <p>Modal content</p>
      <button onClick={() => setShowModal(false)}>Close</button>
    </div>
  </div>
)}`, 'modal-code')}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
              >
                {copiedCode === 'modal-code' ? (
                  <><CheckCircle size={14} /> Copied!</>
                ) : (
                  <><Copy size={14} /> Copy Code</>
                )}
              </button>
            </div>
            <pre className="text-sm text-gray-800 overflow-x-auto">
              <code>{`{showModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4">
      <h3>Modal Title</h3>
      <p>Modal content</p>
      <button onClick={() => setShowModal(false)}>Close</button>
    </div>
  </div>
)}`}</code>
            </pre>
          </div>
        </StandardCard>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-teal-700 to-teal-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Component Showcase & Documentation
            </h1>
            <p className="text-xl text-teal-100">
              Interactive reference for ValueBook's React design system with live previews and code snippets
            </p>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex overflow-x-auto">
            {tabs?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={cn(
                    'flex items-center gap-2 px-6 py-4 font-semibold text-sm whitespace-nowrap border-b-2 transition-colors',
                    activeTab === tab?.id
                      ? 'border-teal-600 text-teal-600' 
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  )}
                >
                  <Icon size={18} />
                  {tab?.label}
                </button>
              );
            })}
          </nav>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'colors' && <ColorTokenSection />}
          {activeTab === 'buttons' && <ButtonSection />}
          {activeTab === 'forms' && <FormSection />}
          {activeTab === 'cards' && <CardSection />}
          {activeTab === 'modals' && <ModalSection />}
        </div>
      </section>

      {/* Live Modal Demo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={cn(
            'bg-white rounded-xl shadow-2xl w-full mx-4 animate-scale-in',
            modalSizes?.find(m => m?.size === modalSize)?.width
          )}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                {modalSizes?.find(m => m?.size === modalSize)?.label} Modal
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                This is a {modalSizes?.find(m => m?.size === modalSize)?.label?.toLowerCase()} modal demonstrating the overlay behavior and animation states.
                Modals are used for focused interactions that require user attention.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Modal Features:</h4>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li>Overlay backdrop with opacity</li>
                  <li>Centered positioning</li>
                  <li>Responsive sizing</li>
                  <li>Smooth animations</li>
                  <li>Accessible close button</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <StandardButton variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </StandardButton>
              <StandardButton variant="primary" onClick={() => setShowModal(false)}>
                Confirm
              </StandardButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComponentShowcaseDocumentation;